// Core modules
import angular from 'angular';
import { EN_LOCALE_DATA } from './modules/translate/translationLoaders/localeConstants';

// IMPORTANT: Initialize $locale with English defaults BEFORE Angular bootstrap
// This prevents undefined errors when components like blip-daterange-picker
// try to access $locale.DATETIME_FORMATS during controller initialization
if (!window.ng) window.ng = {};
if (!window.ng.locale) {
    window.ng.locale = EN_LOCALE_DATA;
}

// CRITICAL: DI Polyfill DISABLED - ng-inject-loader should handle DI properly
// The polyfill was causing more issues than it solved, especially with provider injection
// Keeping window.ng.locale initialization above which is still needed for blip-components

if (angular) {
    // Patch Angular's internal annotate function directly
    // Angular uses this internally: angular.injector.$$annotate or via closure
    // We need to patch at the lowest level possible
    
    const minifiedMap = {
        'i': '$q', 'e': '$http', 'n': '$scope', 'a': '$rootScope',
        's': '$timeout', 'r': '$compile', 't': '$injector', 'o': '$location',
        'c': '$state', 'l': '$stateParams', 'd': '$routeParams',
        'u': '$http', 'p': '$parse', 'h': '$controller', 'f': '$filter',
        'g': '$window', 'm': '$document',
    };
    
    const isMinifiedParam = (p) => typeof p === 'string' && p.length === 1 && /^[a-z]$/.test(p);
    
    const hasMinifiedParams = (params) => {
        if (!Array.isArray(params)) return false;
        return params.some(isMinifiedParam);
    };
    
    // APPROACH: Intercept angular.injector creation to patch $injector.invoke
    const originalInjector = angular.injector;
    angular.injector = function(...args) {
        const injector = originalInjector.apply(this, args);
        
        // Patch the invoke method which is where the actual DI resolution happens
        const origInvoke = injector.invoke;
        injector.invoke = function(fn, self, locals, serviceName) {
            // If function has minified single-letter params without $inject, skip it
            // This prevents Angular from trying to resolve 'i', 'e', etc. as services
            if (typeof fn === 'function' && !fn.$inject) {
                const str = fn.toString();
                const match = str.match(/^function[^(]*\(([^)]*)\)/) || str.match(/^\(([^)]*)\)\s*=>/);
                if (match && match[1]) {
                    const params = match[1].split(',').map(p => p.trim().split(/[=?:]/)[0].trim()).filter(p => p);
                    if (hasMinifiedParams(params)) {
                        // Skip this invocation - call without DI args (minified code doesn't need them)
                        try {
                            return fn.call(self);
                        } catch (e) {
                            return undefined;
                        }
                    }
                }
            }
            return origInvoke.apply(this, arguments);
        };
        
        // Also patch instantiate for constructors
        const origInstantiate = injector.instantiate;
        injector.instantiate = function(Type, locals) {
            if (typeof Type === 'function' && !Type.$inject) {
                const str = Type.toString();
                const match = str.match(/^function[^(]*\(([^)]*)\)/);
                if (match && match[1]) {
                    const params = match[1].split(',').map(p => p.trim().split(/[=?:]/)[0].trim()).filter(p => p);
                    if (hasMinifiedParams(params)) {
                        // Silently skip minified constructors
                        try {
                            return new Type();
                        } catch (e) {
                            return {};
                        }
                    }
                }
            }
            return origInstantiate.apply(this, arguments);
        };
        
        return injector;
    };
    
    // ALSO: Patch the bootstrap process to intercept the app injector
    const originalBootstrap = angular.bootstrap;
    angular.bootstrap = function(element, modules, config) {
        // Wrap the $injector service in the modules
        const patchModule = angular.module('$$diPolyfill', []).config(['$provide', function($provide) {
            $provide.decorator('$injector', ['$delegate', function($delegate) {
                const origInvoke = $delegate.invoke;
                const origInstantiate = $delegate.instantiate;
                const origAnnotate = $delegate.annotate;
                
                $delegate.annotate = function(fn, strictDi, name) {
                    if (typeof fn === 'function' && !fn.$inject) {
                        const str = fn.toString();
                        const match = str.match(/^function[^(]*\(([^)]*)\)/) || str.match(/^\(([^)]*)\)\s*=>/);
                        if (match && match[1]) {
                            const params = match[1].split(',').map(p => p.trim().split(/[=?:]/)[0].trim()).filter(p => p);
                            if (hasMinifiedParams(params)) {
                                // Return empty array to prevent Angular from resolving minified params
                                return [];
                            }
                        }
                    }
                    return origAnnotate.apply(this, arguments);
                };
                
                $delegate.invoke = function(fn, self, locals, serviceName) {
                    if (typeof fn === 'function' && !fn.$inject) {
                        const str = fn.toString();
                        const match = str.match(/^function[^(]*\(([^)]*)\)/) || str.match(/^\(([^)]*)\)\s*=>/);
                        if (match && match[1]) {
                            const params = match[1].split(',').map(p => p.trim().split(/[=?:]/)[0].trim()).filter(p => p);
                            if (hasMinifiedParams(params)) {
                                // Silently skip - this is expected for minified third-party code
                                try { return fn.call(self); } catch(e) { return undefined; }
                            }
                        }
                    }
                    return origInvoke.apply(this, arguments);
                };
                
                $delegate.instantiate = function(Type, locals) {
                    if (typeof Type === 'function' && !Type.$inject) {
                        const str = Type.toString();
                        const match = str.match(/^function[^(]*\(([^)]*)\)/);
                        if (match && match[1]) {
                            const params = match[1].split(',').map(p => p.trim().split(/[=?:]/)[0].trim()).filter(p => p);
                            if (hasMinifiedParams(params)) {
                                // Silently skip - this is expected for minified third-party code
                                try { return new Type(); } catch(e) { return {}; }
                            }
                        }
                    }
                    return origInstantiate.apply(this, arguments);
                };
                
                return $delegate;
            }]);
        }]);
        
        // Prepend our patch module
        const patchedModules = ['$$diPolyfill', ...(modules || [])];
        return originalBootstrap.call(this, element, patchedModules, config);
    };
    
    console.log('[DI-Polyfill] INITIALIZED - bootstrap and injector patched');
}

import modules from './modules';
import constants from './app.constants';
import states from './app.states';
import { core } from 'modules/core';
import 'angular-animate';
import 'angular-sanitize';
import 'angular-cookies';

// UI modules
import 'angular-clipboard';
import 'angular-tooltips';
import 'angular-input-masks';
import 'angular-datetime-input';
import 'angular-touch';
import 'angularjs-slider';
import 'ng-tags-input';
import 'angular-wizard';
import 'ng-toast';
import 'ng-onload';
import 'angular-svg-round-progressbar';
import 'angucomplete-alt';
import 'angular-messages';
import 'angular-legacy-sortablejs-maintained';
import 'assets/js/ng-context-menu';

import moment from 'moment';
import 'moment/locale/pt-br';

// Cards
import vue from 'vue';
import 'ngVue';
import BlipCards from 'blip-cards';
const components = BlipCards.install(vue);

// Toolkit
import 'blip-cards/dist/blip-cards.css';
import 'blip-toolkit/dist/blip-toolkit.css';
import 'blip-components/dist/blip-components.css';

// Misc
import { UnauthorizedAccessEvent } from 'application/detail/DetailApplicationController';
import { getCookie } from 'data/cookies';
import { builderStateName } from 'modules/application/detail/portal-submodule-builder/templates/builder';
import { pipelineStateName } from 'modules/application/detail/portal-submodule-builder-pipeline/templates/builder';

import { AppCuesService } from 'modules/shared/AppCuesService';
import { MicroFrontendService } from 'modules/shared/MicroFrontendService';
import { startSentry } from '../config/sentryConfig';

import { v4 as uuid } from 'uuid';
import { logoutStateName } from 'modules/login';
import { LSLastUrlTry } from 'modules/account';

import { FeatureToggleClientService } from 'feature-toggle-client';
import { sessionExpiry } from '../sessionManager/SessionManager';

const app = angular
    .module('app', [
        'ngLocale',
        'angular-clipboard',
        'ngAnimate',
        'ngMessages',
        'ngSanitize',
        '720kb.tooltips',
        'ui.utils.masks',
        'datetime',
        'ngTouch',
        'mgo-angular-wizard',
        'ngTagsInput',
        'ngToast',
        'angucomplete-alt',
        'ngCookies',
        'rzModule',
        'angular-svg-round-progressbar',
        'ngVue',
        'ngOnload',
        'ng-sortable',
        'ng-context-menu',
        constants,
        modules,
        states,
        core,
    ])
    .run(['$locale', function ensureLocaleFormats($locale) {
        // Ensure $locale and its DATETIME_FORMATS exist for blip-components
        if (!$locale || typeof $locale.DATETIME_FORMATS !== 'object') {
            $locale.DATETIME_FORMATS = {
                AMPMS: ['AM', 'PM'],
                DAY: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                ERANAMES: ['Before Christ', 'Anno Domini'],
                ERAS: ['BC', 'AD'],
                FIRSTDAYOFWEEK: 6,
                MONTH: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                SHORTDAY: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                SHORTMONTH: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                STANDALONEMONTH: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                WEEKENDRANGE: [5, 6],
                fullDate: 'EEEE, MMMM d, y',
                longDate: 'MMMM d, y',
                medium: 'MMM d, y h:mm:ss a',
                mediumDate: 'MMM d, y',
                mediumTime: 'h:mm:ss a',
                short: 'M/d/yy h:mm a',
                shortDate: 'M/d/yy',
                shortTime: 'h:mm a',
            };
        }
    }])
    .config(['ngToastProvider', 'NG_TRANSLATE_LANG_KEY', '$compileProvider', 'AI_IS_MFE_ENABLED', 'AI_MFE_SCRIPT_SOURCE', '$provide',
        function(
            ngToastProvider,
            NG_TRANSLATE_LANG_KEY,
            $compileProvider,
            AI_IS_MFE_ENABLED,
            AI_MFE_SCRIPT_SOURCE,
            $provide,
        ) {
            // CRITICAL: Decorate $locale to ensure DATETIME_FORMATS exists BEFORE any component initializes
            // This fixes the blip-components daterangepicker TypeError
            $provide.decorator('$locale', ['$delegate', function($delegate) {
                if (!$delegate.DATETIME_FORMATS) {
                    $delegate.DATETIME_FORMATS = {
                        AMPMS: ['AM', 'PM'],
                        DAY: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                        ERANAMES: ['Before Christ', 'Anno Domini'],
                        ERAS: ['BC', 'AD'],
                        FIRSTDAYOFWEEK: 6,
                        MONTH: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                        SHORTDAY: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                        SHORTMONTH: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                        STANDALONEMONTH: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                        WEEKENDRANGE: [5, 6],
                        fullDate: 'EEEE, MMMM d, y',
                        longDate: 'MMMM d, y',
                        medium: 'MMM d, y h:mm:ss a',
                        mediumDate: 'MMM d, y',
                        mediumTime: 'h:mm:ss a',
                        short: 'M/d/yy h:mm a',
                        shortDate: 'M/d/yy',
                        shortTime: 'h:mm a',
                    };
                }
                return $delegate;
            }]);

            $compileProvider.debugInfoEnabled(false);

            ngToastProvider.configure({
                verticalPosition: 'bottom',
                horizontalPosition: 'left',
                animation: 'slide',
                timeout: 5000,
                dismissButton: true,
                maxNumber: 6,
            });

            let userLanguage = getCookie(NG_TRANSLATE_LANG_KEY);
            userLanguage = userLanguage === 'pt' ? 'pt-BR' : userLanguage;
            moment.locale(userLanguage);
            if (AI_IS_MFE_ENABLED) {
                MicroFrontendService.addSource(AI_MFE_SCRIPT_SOURCE);
            }
        },
    ])
    .run(['$rootScope', '$location', '$state', '$http', 'LoadingService', 'AuthenticationService', 'AccountService2', 'BlipService', '$timeout', 'TranslateService', 'InitializeUserService', 'DiscourseValidationService', 'OidcService', 'SEGMENT_TRACKING_APP_KEY', 'GOOGLE_TAGMANAGER_KEY', 'HOTJAR',
        function(
            $rootScope,
            $location,
            $state,
            $http,
            LoadingService,
            AuthenticationService,
            AccountService2,
            BlipService,
            $timeout,
            TranslateService,
            InitializeUserService,
            DiscourseValidationService,
            OidcService,
            SEGMENT_TRACKING_APP_KEY,
            GOOGLE_TAGMANAGER_KEY,
            HOTJAR,
        ) {
            let locationSearch;
            $rootScope.$state = $state;
            $rootScope.googleTagmanagerKey = GOOGLE_TAGMANAGER_KEY;

            const analytics = (window.analytics = window.analytics || []);
            if (!analytics.initialize) {
                if (analytics.invoked) {
                    window.console &&
                        console.error && //eslint-disable-line
                        console.error('Segment snippet included twice.'); //eslint-disable-line
                } else {
                    analytics.invoked != 0;
                    analytics.methods = [
                        'trackSubmit',
                        'trackClick',
                        'trackLink',
                        'trackForm',
                        'pageview',
                        'identify',
                        'reset',
                        'group',
                        'track',
                        'ready',
                        'alias',
                        'debug',
                        'page',
                        'once',
                        'off',
                        'on',
                    ];
                    analytics.factory = function (t) {
                        return function () {
                            const e = Array.prototype.slice.call(arguments);
                            e.unshift(t);
                            analytics.push(e);
                            return analytics;
                        };
                    };
                    for (let t = 0; t < analytics.methods.length; t++) {
                        const e = analytics.methods[t];
                        analytics[e] = analytics.factory(e);
                    }
                    analytics.load = function (t) {
                        const e = document.createElement('script');
                        e.type = 'text/javascript';
                        e.async = !0;
                        e.src =
                            ('https:' === document.location.protocol
                                ? 'https://'
                                : 'http://') +
                            'cdn.segment.com/analytics.js/v1/' +
                            t +
                            '/analytics.min.js';
                        const n = document.getElementsByTagName('script')[0];
                        n.parentNode.insertBefore(e, n);
                    };
                    analytics.SNIPPET_VERSION = '4.0.0';
                }
            }
            analytics.load(SEGMENT_TRACKING_APP_KEY);

            // Google tag manager
            (function (w, d, s, i) {
                const f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s);
                j.async = true;
                j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i;
                f.parentNode.insertBefore(j, f);
            })(window, document, 'script', GOOGLE_TAGMANAGER_KEY);

            // Hotjar
            if (HOTJAR && HOTJAR.enabled && String(true) === HOTJAR.enabled.toLowerCase()) {
                (function (h, o, t, j, k) {
                    h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
                    h._hjSettings = { hjid: k, hjsv: 6 };
                    const a = o.getElementsByTagName('head')[0];
                    const r = o.createElement('script'); r.async = 1;
                    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
                    a.appendChild(r);
                })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=', HOTJAR.key);
            }

            $rootScope.$on(
                '$stateChangeStart',
                async (e, toState, toParams, from) => {
                    window.scrollTo(0, 0);
                    $state.from = from; //in case we need

                    locationSearch = encodeURI($location.search());

                    if (toState.redirectTo) {
                        e.preventDefault();
                        $state.go(toState.redirectTo, toParams);
                    }

                    if (
                        toState.data &&
                        toState.data.restricted === true &&
                        !AuthenticationService.isAuthenticated()
                    ) {
                        e.preventDefault();
                        LoadingService.stopLoading();

                        const { origin, href } = { ...window.location };
                        // Set the url that user try to visit
                        if (['logout', 'authorize'].some(p => href.includes(p))) {
                            localStorage.setItem(LSLastUrlTry, origin);
                        } else {
                            localStorage.setItem(LSLastUrlTry, href);
                        }

                        $state.go('anon.login', {
                            redirectUrl: { toState, toParams },
                            notify: false,
                            currentState: $state.current,
                            currentParams: $state.params,
                        });
                    } else if (
                        toState.name.indexOf('anon') !== -1 &&
                        AuthenticationService.isAuthenticated() &&
                        !locationSearch.redirectUrl &&
                        (toParams && !toParams.preventRedirect)
                    ) {

                        const payload = locationSearch.sso;
                        const sig = locationSearch.sig;

                        const blipConnected = await BlipService.isConnected();
                        if (locationSearch.provider) {
                            if (!(blipConnected)) {
                                await BlipService.connect(
                                    encodeURIComponent(AuthenticationService.email),
                                    AuthenticationService.token,
                                );
                            }

                            const ssoResponse = await BlipService.sendCommand({
                                id: uuid(),
                                to: 'postmaster@portal.blip.ai',
                                method: 'get',
                                uri: `/sso/${locationSearch.provider}`,
                            });

                            window.location.href = ssoResponse.redirectUri;
                            return;
                        }
                        await DiscourseValidationService.redirectToDiscourse(
                            payload,
                            sig,
                        );

                        e.preventDefault();
                        LoadingService.stopLoading();
                        $state.go('auth.application.list');
                    } else if (
                        locationSearch.redirectUrl &&
                        await AuthenticationService.isServerAuthenticated() &&
                        toState.name != logoutStateName
                    ) {
                        const redirectUrl = AuthenticationService.getAuthRedirectUrl(
                            locationSearch.redirectUrl,
                        );
                        window.location = redirectUrl;
                    }

                    if (!$rootScope.featureToggleInitialized) {
                        await InitializeUserService.initFeatureToggle();

                        // trigger session expiry timer if feature is enabled
                        sessionExpiry();
                    }
                },
            );

            $rootScope.$on('$stateChangeSuccess', async (_, toState, toParams) => {
                window.scrollTo(0, 0);

                if (toState && toState.name.startsWith('auth')) {
                    try {
                        const fetchAndGetAccount = async () => {
                            await AccountService2.fetchAndSetAccount();
                            return AuthenticationService.getAccount();
                        };

                        const account = AuthenticationService.getAccount() ? AuthenticationService.getAccount() : await fetchAndGetAccount();
                        const { fullName: name, culture, email, creationDate } = account;

                        if (culture) {
                            TranslateService.setCurrentLanguage(culture);
                        }

                        AppCuesService
                            .instance
                            .identify(email, {
                                name,
                                email,
                                createdAt: moment.utc(creationDate).format('YYYY-MM-DD'),
                            });
                    } catch (e) {
                        console.error(e); //eslint-disable-line
                    }
                }

                /**
                 * Forbidden Appcues initializers means that start will be made by its own controller/state
                 * or that's not necessary start Appcues script
                 */

                const forbiddenAppcueInitializers = [builderStateName, pipelineStateName];
                if (!forbiddenAppcueInitializers.includes(toState.name)) {
                    $timeout(() => AppCuesService.instance.start());
                }

                if (toParams.shortName) {
                    const application = $rootScope.application;
                    const { permissionClaim = undefined } =
                        $state.current.data || {};

                    if (
                        permissionClaim &&
                        !application.applicationUserPermissionModel.some(
                            (p) =>
                                p.permissionClaim === permissionClaim &&
                                p.permissionAction > 0,
                        )
                    ) {
                        $rootScope.$broadcast(UnauthorizedAccessEvent, {
                            application: application,
                        });
                    }
                }

                if (toState.data && toState.data.keepUrl) {
                    $location.search(locationSearch);
                }

            });

            $rootScope.$on('$stateNotFound', LoadingService.stopLoading);
            $rootScope.$on('$stateChangeError', LoadingService.stopLoading);
        },
    ]);

app.controller('SentryController', ['InitializeUserService', async function (InitializeUserService) {
    await InitializeUserService.initFeatureToggle();
    await FeatureToggleClientService.getInstance().isFeatureEnabled('sentry-configuration').
        then((sentryData) => {
            if (sentryData && sentryData.isEnabled) {
                const releaseMetaTag = document.querySelector('meta[name="release-version"]');
                if (releaseMetaTag && releaseMetaTag.content != 'RELEASE_PLACEHOLDER') {
                    console.log(releaseMetaTag.content); //eslint-disable-line
                    sentryData.init.release = releaseMetaTag.content;
                }
                startSentry(sentryData);
            }
        });
}]);

for (const component of components) {
    const name = component.extendOptions.name.replace(/[-_]([a-z])/g, function (
        g,
    ) {
        return g[1].toUpperCase();
    });
    app.value(name, component);
}

export default app;
